import React from 'react';
import { trackWhatsAppClick } from '../utils/tracking';
import { toWhatsAppPhone } from '../utils/whatsappPhone';
import { withOriginCode } from '../utils/siteEvents';
import styled from 'styled-components';
import WhatsAppIcon from './icons/WhatsAppIcon';
import { Broker } from '../types';

const ContactButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const ContactButton = styled.a<{ $variant: 'phone' | 'email' | 'whatsapp' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  text-decoration: none;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  white-space: nowrap;
  
  ${({ $variant, theme }) => {
    switch ($variant) {
      case 'phone':
        return `
          background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary});
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
          }
        `;
      case 'email':
        return `
          background: linear-gradient(135deg, #6366f1, #4f46e5);
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
          }
        `;
      case 'whatsapp':
        return `
          background: linear-gradient(135deg, #25d366, #128c7e);
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(37, 211, 102, 0.4);
          }
        `;
    }
  }}
`;

interface BrokerContactButtonsProps {
  broker: Broker;
  propertyTitle?: string;
  className?: string;
}

const BrokerContactButtons: React.FC<BrokerContactButtonsProps> = ({ 
  broker, 
  propertyTitle,
  className 
}) => {
  const getWhatsAppMessage = () => {
    const baseMessage = `Olá ${broker.name}!`;
    if (propertyTitle) {
      return `${baseMessage} Vi o imóvel "${propertyTitle}" no site e gostaria de mais informações.`;
    }
    return `${baseMessage} Gostaria de mais informações sobre imóveis disponíveis.`;
  };

  return (
    <ContactButtons className={className}>
      {broker.phone && (
        <>
          <ContactButton 
            href={`tel:${broker.phone}`}
            $variant="phone"
          >
            📞 Ligar
          </ContactButton>
          
          <ContactButton 
            href={`https://wa.me/${toWhatsAppPhone(broker.phone)}?text=${encodeURIComponent(withOriginCode(getWhatsAppMessage()))}`}
            target="_blank"
            rel="noopener noreferrer"
            $variant="whatsapp"
            onClick={() => trackWhatsAppClick('broker_contact')}
          >
            <WhatsAppIcon size={16} color="white" />
            WhatsApp
          </ContactButton>
        </>
      )}
      
      {broker.email && (
        <ContactButton 
          href={`mailto:${broker.email}${propertyTitle ? `?subject=Interesse no imóvel: ${propertyTitle}` : ''}`}
          $variant="email"
        >
          ✉️ Email
        </ContactButton>
      )}
    </ContactButtons>
  );
};

export default BrokerContactButtons;